import React from "react";
import "./or-input.css";
import { useOrInput } from "./or-input.hook";

export type OrInputVariant = "default" | "soft" | "ghost";
export type OrInputTheme = "default" | "social";
export type OrInputType = "text" | "email" | "password" | "number" | "tel" | "url" | "time" | "textarea";
export type OrInputSize = "xs" | "sm" | "md" | "lg";

interface OrInputBaseProps {
  errorText?: React.ReactNode;
  fullWidth?: boolean;
  hasError?: boolean;
  helperText?: React.ReactNode;
  label?: React.ReactNode;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  size?: OrInputSize;
  theme?: OrInputTheme;
  variant?: OrInputVariant;
}

type OrInputFieldProps = OrInputBaseProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type"> & {
    inputType?: Exclude<OrInputType, "textarea">;
  };

type OrInputTextareaProps = OrInputBaseProps &
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> & {
    inputType: "textarea";
  };

export type OrInputProps = OrInputFieldProps | OrInputTextareaProps;

type TimeSegment = "hours" | "minutes";
type TimeSegmentInputBuffer = {
  digits: string;
  segment: TimeSegment | null;
};

const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = HOURS_IN_DAY * MINUTES_IN_HOUR;

function resolveInputValue(value: string | number | readonly string[] | undefined): string {
  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
}

function normalizeTimeValue(value: string): string | null {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  const match = trimmedValue.match(/^(\d{1,2})(?::?(\d{2}))\s*([aApP][mM])?$/);

  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? "0");
  const meridiem = match[3]?.toUpperCase() ?? null;

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  let normalizedHours = hours;

  if (meridiem === "AM" && hours >= 1 && hours <= 12) {
    normalizedHours = hours === 12 ? 0 : hours;
  }

  if (meridiem === "PM" && hours >= 1 && hours <= 12) {
    normalizedHours = hours === 12 ? 12 : hours + 12;
  }

  return `${String(normalizedHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function resolveTimeDisplayValue(value: string | number | readonly string[] | undefined): string {
  const resolvedValue = resolveInputValue(value);
  const normalizedValue = normalizeTimeValue(resolvedValue);

  return normalizedValue ?? resolvedValue;
}

function parseTimeValue(value: string): { hours: number; minutes: number } | null {
  const normalizedValue = normalizeTimeValue(value);

  if (!normalizedValue) {
    return null;
  }

  const [hoursSegment, minutesSegment] = normalizedValue.split(":");
  const hours = Number(hoursSegment);
  const minutes = Number(minutesSegment);

  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null;
  }

  return { hours, minutes };
}

function formatTimeValue(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function getTimeSegment(selectionStart: number | null, fallbackSegment: TimeSegment): TimeSegment {
  if (selectionStart === null) {
    return fallbackSegment;
  }

  return selectionStart <= 2 ? "hours" : "minutes";
}

function selectTimeSegment(input: HTMLInputElement, segment: TimeSegment): void {
  if (segment === "hours") {
    input.setSelectionRange(0, 2);
    return;
  }

  input.setSelectionRange(3, 5);
}

function stepTimeValue(value: string, segment: TimeSegment, amount: number): string {
  const timeParts = parseTimeValue(value) ?? { hours: 0, minutes: 0 };

  if (segment === "hours") {
    const nextHours = (timeParts.hours + amount + HOURS_IN_DAY) % HOURS_IN_DAY;

    return formatTimeValue(nextHours, timeParts.minutes);
  }

  const totalMinutes = timeParts.hours * MINUTES_IN_HOUR + timeParts.minutes + amount;
  const normalizedTotalMinutes = ((totalMinutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const nextHours = Math.floor(normalizedTotalMinutes / MINUTES_IN_HOUR);
  const nextMinutes = normalizedTotalMinutes % MINUTES_IN_HOUR;

  return formatTimeValue(nextHours, nextMinutes);
}

function replaceTimeSegmentValue(value: string, segment: TimeSegment, segmentValue: string): string {
  const timeParts = parseTimeValue(value) ?? { hours: 0, minutes: 0 };
  const normalizedSegmentValue = String(Number(segmentValue)).padStart(2, "0");

  if (segment === "hours") {
    return formatTimeValue(Number(normalizedSegmentValue), timeParts.minutes);
  }

  return formatTimeValue(timeParts.hours, Number(normalizedSegmentValue));
}

function resolveTimeSegmentInput(
  value: string,
  segment: TimeSegment,
  digit: string,
  currentBuffer: TimeSegmentInputBuffer,
): { nextBuffer: TimeSegmentInputBuffer; nextValue: string } {
  const maxValue = segment === "hours" ? 23 : 59;
  const previousDigits = currentBuffer.segment === segment ? currentBuffer.digits : "";
  const candidateDigits = `${previousDigits}${digit}`.slice(-2);
  const candidateValue = Number(candidateDigits);

  if (candidateDigits.length === 2 && candidateValue <= maxValue) {
    return {
      nextBuffer: { digits: "", segment },
      nextValue: replaceTimeSegmentValue(value, segment, candidateDigits),
    };
  }

  return {
    nextBuffer: { digits: digit, segment },
    nextValue: replaceTimeSegmentValue(value, segment, digit),
  };
}

function createTimeChangeEvent(target: HTMLInputElement, value: string): React.ChangeEvent<HTMLInputElement> {
  return {
    currentTarget: {
      ...target,
      value,
    },
    target: {
      ...target,
      value,
    },
  } as React.ChangeEvent<HTMLInputElement>;
}

function renderMessage(
  hasError: boolean,
  errorText?: React.ReactNode,
  helperText?: React.ReactNode,
): React.ReactNode | null {
  if (hasError && errorText) {
    return <p className="or-input__message or-input__message--error">{errorText}</p>;
  }

  if (!hasError && helperText) {
    return <p className="or-input__message">{helperText}</p>;
  }

  return null;
}

export function OrInput(props: OrInputProps): React.ReactElement {
  const reactId = React.useId();
  const { className, errorText, fullWidth, hasError = false, helperText, size, theme, variant } = props;
  const { inputClassName, wrapperClassName } = useOrInput({ className, fullWidth, hasError, size, theme, variant });
  const message = renderMessage(hasError, errorText, helperText);

  if (props.inputType === "textarea") {
    const {
      className: _className,
      errorText: _errorText,
      fullWidth: _fullWidth,
      hasError: _hasError,
      helperText: _helperText,
      id,
      inputType,
      label,
      leftSlot,
      rightSlot,
      size: _size,
      theme: _theme,
      variant: _variant,
      ...textareaProps
    } = props;
    const inputId = id ?? `or-input-${reactId.replace(/:/g, "")}`;
    const controlClassName = ["or-input__control", "or-input__control--textarea"].filter(Boolean).join(" ");
    const fieldClassName = [inputClassName, "or-input__field--textarea"].join(" ");

    return (
      <div className={wrapperClassName}>
        {label ? (
          <label className="or-input__label" htmlFor={inputId}>
            {label}
          </label>
        ) : null}
        <div className={controlClassName}>
          {leftSlot ? <span className="or-input__slot or-input__slot--left">{leftSlot}</span> : null}
          <textarea className={fieldClassName} id={inputId} {...textareaProps} />
          {rightSlot ? <span className="or-input__slot or-input__slot--right">{rightSlot}</span> : null}
        </div>
        {message}
      </div>
    );
  }

  const {
    className: _className,
    errorText: _errorText,
    fullWidth: _fullWidth,
    hasError: _hasError,
    helperText: _helperText,
    id,
    inputType = "text",
    label,
    leftSlot,
    rightSlot,
    size: _size,
    theme: _theme,
    variant: _variant,
    ...inputProps
  } = props;
  const inputId = id ?? `or-input-${reactId.replace(/:/g, "")}`;
  const fieldClassName = [inputClassName, inputType === "time" ? "or-input__field--time" : ""]
    .filter(Boolean)
    .join(" ");

  if (inputType === "time") {
    const { defaultValue, onBlur, onChange, onClick, onFocus, onKeyDown, onSelect, value, ...timeInputProps } =
      inputProps;
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const pendingSelectionSegmentRef = React.useRef<TimeSegment | null>(null);
    const timeSegmentInputBufferRef = React.useRef<TimeSegmentInputBuffer>({ digits: "", segment: null });
    const [activeSegment, setActiveSegment] = React.useState<TimeSegment>("hours");
    const [timeDisplayValue, setTimeDisplayValue] = React.useState(() =>
      resolveTimeDisplayValue(value ?? defaultValue),
    );

    React.useEffect(() => {
      if (value === undefined) {
        return;
      }

      setTimeDisplayValue(resolveTimeDisplayValue(value));
    }, [value]);

    const emitNormalizedTimeChange = React.useCallback(
      (target: HTMLInputElement, nextValue: string): void => {
        onChange?.(createTimeChangeEvent(target, nextValue));
      },
      [onChange],
    );

    const handleTimeChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        const nextDisplayValue = event.currentTarget.value;
        const normalizedValue = normalizeTimeValue(nextDisplayValue);

        timeSegmentInputBufferRef.current = { digits: "", segment: null };
        setTimeDisplayValue(nextDisplayValue);
        emitNormalizedTimeChange(event.currentTarget, normalizedValue ?? "");
      },
      [emitNormalizedTimeChange],
    );

    const syncTimeSegmentSelection = React.useCallback((segment: TimeSegment): void => {
      pendingSelectionSegmentRef.current = segment;

      if (!inputRef.current || document.activeElement !== inputRef.current) {
        return;
      }

      selectTimeSegment(inputRef.current, segment);
    }, []);

    React.useLayoutEffect(() => {
      if (!inputRef.current || document.activeElement !== inputRef.current || !pendingSelectionSegmentRef.current) {
        return;
      }

      selectTimeSegment(inputRef.current, pendingSelectionSegmentRef.current);
      pendingSelectionSegmentRef.current = null;
    }, [activeSegment, timeDisplayValue]);

    const handleTimeSegmentSelection = React.useCallback(
      (input: HTMLInputElement, segment: TimeSegment): void => {
        timeSegmentInputBufferRef.current = { digits: "", segment };
        setActiveSegment(segment);
        syncTimeSegmentSelection(segment);

        if (document.activeElement === input) {
          selectTimeSegment(input, segment);
        }
      },
      [syncTimeSegmentSelection],
    );

    const handleTimeFocus = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>): void => {
        onFocus?.(event);

        if (event.defaultPrevented || timeInputProps.disabled || timeInputProps.readOnly) {
          return;
        }

        handleTimeSegmentSelection(event.currentTarget, "hours");
      },
      [handleTimeSegmentSelection, onFocus, timeInputProps.disabled, timeInputProps.readOnly],
    );

    const handleTimeClick = React.useCallback(
      (event: React.MouseEvent<HTMLInputElement>): void => {
        const nextSegment = getTimeSegment(event.currentTarget.selectionStart, activeSegment);

        onClick?.(event);

        if (event.defaultPrevented || timeInputProps.disabled || timeInputProps.readOnly) {
          return;
        }

        handleTimeSegmentSelection(event.currentTarget, nextSegment);
      },
      [activeSegment, handleTimeSegmentSelection, onClick, timeInputProps.disabled, timeInputProps.readOnly],
    );

    const handleTimeSelect = React.useCallback(
      (event: React.SyntheticEvent<HTMLInputElement>): void => {
        setActiveSegment(getTimeSegment(event.currentTarget.selectionStart, activeSegment));
        onSelect?.(event);
      },
      [activeSegment, onSelect],
    );

    const handleTimeKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>): void => {
        const currentSegment = activeSegment;

        onKeyDown?.(event);

        if (event.defaultPrevented || timeInputProps.disabled || timeInputProps.readOnly) {
          return;
        }

        if (event.key === "Tab") {
          if (!event.shiftKey && currentSegment === "hours") {
            event.preventDefault();
            handleTimeSegmentSelection(event.currentTarget, "minutes");
          }

          if (event.shiftKey && currentSegment === "minutes") {
            event.preventDefault();
            handleTimeSegmentSelection(event.currentTarget, "hours");
          }

          return;
        }

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          handleTimeSegmentSelection(event.currentTarget, "hours");
          return;
        }

        if (event.key === "ArrowRight") {
          event.preventDefault();
          handleTimeSegmentSelection(event.currentTarget, "minutes");
          return;
        }

        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          event.preventDefault();

          const amount = event.key === "ArrowUp" ? 1 : -1;
          const nextValue = stepTimeValue(event.currentTarget.value, currentSegment, amount);

          timeSegmentInputBufferRef.current = { digits: "", segment: currentSegment };
          setActiveSegment(currentSegment);
          setTimeDisplayValue(nextValue);
          emitNormalizedTimeChange(event.currentTarget, nextValue);
          syncTimeSegmentSelection(currentSegment);
          return;
        }

        if (/^\d$/.test(event.key) && !event.altKey && !event.ctrlKey && !event.metaKey) {
          event.preventDefault();

          const { nextBuffer, nextValue } = resolveTimeSegmentInput(
            event.currentTarget.value,
            currentSegment,
            event.key,
            timeSegmentInputBufferRef.current,
          );

          timeSegmentInputBufferRef.current = nextBuffer;
          setActiveSegment(currentSegment);
          setTimeDisplayValue(nextValue);
          emitNormalizedTimeChange(event.currentTarget, nextValue);
          syncTimeSegmentSelection(currentSegment);
        }
      },
      [
        activeSegment,
        emitNormalizedTimeChange,
        handleTimeSegmentSelection,
        onKeyDown,
        syncTimeSegmentSelection,
        timeInputProps.disabled,
        timeInputProps.readOnly,
      ],
    );

    const handleTimeBlur = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>): void => {
        const normalizedValue = normalizeTimeValue(event.currentTarget.value);

        timeSegmentInputBufferRef.current = { digits: "", segment: null };

        if (normalizedValue !== null) {
          setTimeDisplayValue(normalizedValue);
        }

        onBlur?.(event);
      },
      [onBlur],
    );

    return (
      <div className={wrapperClassName}>
        {label ? (
          <label className="or-input__label" htmlFor={inputId}>
            {label}
          </label>
        ) : null}
        <div className="or-input__control">
          {leftSlot ? <span className="or-input__slot or-input__slot--left">{leftSlot}</span> : null}
          <input
            {...timeInputProps}
            autoComplete="off"
            className={fieldClassName}
            id={inputId}
            inputMode="text"
            onClick={handleTimeClick}
            onBlur={handleTimeBlur}
            onChange={handleTimeChange}
            onFocus={handleTimeFocus}
            onKeyDown={handleTimeKeyDown}
            onSelect={handleTimeSelect}
            placeholder={timeInputProps.placeholder ?? "09:00"}
            ref={inputRef}
            type="text"
            value={timeDisplayValue}
          />
          {rightSlot ? <span className="or-input__slot or-input__slot--right">{rightSlot}</span> : null}
        </div>
        {message}
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      {label ? (
        <label className="or-input__label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <div className="or-input__control">
        {leftSlot ? <span className="or-input__slot or-input__slot--left">{leftSlot}</span> : null}
        <input className={fieldClassName} id={inputId} type={inputType} {...inputProps} />
        {rightSlot ? <span className="or-input__slot or-input__slot--right">{rightSlot}</span> : null}
      </div>
      {message}
    </div>
  );
}
